import getConfig from 'next/config'
import { sendEvent } from './coreAnalytics'
import {
  CarouselProduct,
  CarouselProductTypes,
} from '@src/components/DynamicProductCarousel/DynamicProductCardTypes'
import { buildGaPromoObject, makeListIdAndNameFields } from './analyticsUtils'
import { sendTealiumCarouselPromotionEvent } from '../tealiumAnalytics'

const {
  publicRuntimeConfig: { isTealiumEnabled, isGoogleTagManagerEnabled },
} = getConfig()

// Field that must be attached to 'items' on the ecommerce portion of a GA4 Promo Event
type GA4ProductPromoItem = {
  creative_name?: string
  index: number
  item_brand?: string
  item_id?: string
  item_list_id: string
  item_list_name: string
  promotion_id: string
  promotion_name: string
}

/*
  Promo Item has normal GA4 event plus some extra creative slot fields.
  Some of these are Tealium specific.
  eCommerce object with special promo items is then attached to this.
*/
type GA4MasterPromoItem = {
  event: 'view_promotion'
  ecommerce: {
    creative_slot: string
    items: GA4ProductPromoItem[]
  }
}

type GA4CarouselEventInputs = {
  products: CarouselProduct[]
  creativeSlot: string
  carouselType?: CarouselProductTypes
  promoItemId?: string | null
  promoItemName?: string | null
}

type Ga4CarouselProductClickInputs = Omit<
  GA4CarouselEventInputs,
  'products'
> & {
  product: CarouselProduct
  indexPosition?: number
  selectType?: 'select_promotion' | 'select_item'
}

export const sendCarouselPromoEvent = ({
  creativeSlot,
  products,
  carouselType,
  promoItemId,
  promoItemName,
}: GA4CarouselEventInputs) => {
  const promotedItems: GA4ProductPromoItem[] = products.map(
    (product, index) => {
      // On sliced in view arrays we need to give the analytics calculated position (basically original index value + 1). Set that when slicing.
      // In cases where we have all the data and don't supply the analyticsPosition just use the index + 1 to get the human position.
      const viewableIndex = product.analyticsPosition || index + 1

      return buildGaPromoObject(
        product?.gaProductCode,
        product,
        viewableIndex,
        carouselType,
        promoItemId,
        promoItemName
      )
    }
  )

  const builtPromoItem: GA4MasterPromoItem = {
    event: 'view_promotion',
    ecommerce: {
      creative_slot: creativeSlot.toLowerCase(),
      items: promotedItems,
    },
  }
  if (isGoogleTagManagerEnabled) {
    sendEvent({ payload: builtPromoItem })
  }
  // Send carousel view for Tealium when it is enabled.
  if (isTealiumEnabled) {
    sendTealiumCarouselPromotionEvent(
      'view_promotion',
      products,
      creativeSlot,
      carouselType,
      promoItemId,
      promoItemName
    )
  }
}

export const sendCarouselItemClickEvent = ({
  product,
  indexPosition = 0,
  creativeSlot,
  carouselType,
  promoItemId,
  promoItemName,
  selectType = 'select_promotion',
}: Ga4CarouselProductClickInputs) => {
  const viewableIndex = indexPosition + 1

  const builtPromoObj = buildGaPromoObject(
    product?.gaProductCode,
    product,
    viewableIndex,
    carouselType,
    promoItemId,
    promoItemName
  )

  const eventPayload = {
    payload: {
      event: selectType,
      ecommerce: {
        creative_slot: creativeSlot.toLowerCase(),
        items: [builtPromoObj],
      },
    },
  }

  if (selectType === 'select_item') {
    const listItemFields = makeListIdAndNameFields(
      carouselType,
      promoItemId,
      promoItemName
    )

    eventPayload.payload.ecommerce['list_item_id'] = listItemFields.list_id
    eventPayload.payload.ecommerce['list_item_name'] = listItemFields.list_name
  }
  if (isGoogleTagManagerEnabled) {
    sendEvent(eventPayload)
  }

  // Send carousel view for Tealium when it is enabled.
  if (isTealiumEnabled) {
    sendTealiumCarouselPromotionEvent(
      'select_promotion',
      [product],
      creativeSlot,
      carouselType,
      promoItemId,
      promoItemName
    )
  }
}
